import "./styles.css";

import { initI18n } from "./ui/i18n.ts";
import { renderShell } from "./ui/shell.ts";
import { el, tel, StatusLine } from "./ui/dom.ts";
import { t } from "./ui/i18n.ts";
import { createAuthPanel } from "./ui/auth-panel.ts";
import { createSetupPanel } from "./ui/setup-panel.ts";
import { createExportPanel } from "./ui/export-panel.ts";
import { createViewer } from "./ui/viewer/viewer.ts";
import { createFsaViewerSource } from "./ui/viewer/source.ts";
import { initAuth } from "./auth/broker.ts";
import { setState, subscribe } from "./ui/state.ts";

async function main(): Promise<void> {
    initI18n();

    const mount = renderShell();
    if (!mount) {
        // Unsupported browser — the shell has already explained why.
        return;
    }

    // The in-app viewer for browsing already-exported data. Reads from the
    // chosen destination folder (via the shared source) and renders inside
    // the app — never opening files in a new tab, which the File System
    // Access API cannot do without hitting Chromium's blob-navigation block.
    const viewerSource = createFsaViewerSource();
    const viewer = createViewer(viewerSource);
    document.body.append(viewer.root);

    const authPanel = createAuthPanel();
    const setupPanel = createSetupPanel();
    const exportPanel = createExportPanel({ source: viewerSource, openViewer: viewer.open });

    /*
     * Two pages rather than one long scroll:
     *   1. Sign in + choose what to export + approve access (auth & permissions)
     *   2. The export + browse dashboard
     * Both stay mounted (state is shared and live); only visibility toggles,
     * so switching pages never loses in-progress export state.
     */
    const continueButton = tel("button", "nav.continue", { type: "button", class: "primary nav-continue" });
    const continueHint = new StatusLine();
    const authPage = el("div", { class: "page" }, [
        authPanel.root,
        setupPanel.root,
        el("div", { class: "page-nav" }, [continueButton, continueHint.node])
    ]);

    const backButton = tel("button", "nav.back", { type: "button", class: "nav-back" });
    const dashPage = el("div", { class: "page" }, [
        el("div", { class: "page-nav page-nav-top" }, [backButton]),
        exportPanel.root
    ]);

    mount.append(authPage, dashPage);

    const showPage = (name: "auth" | "dashboard"): void => {
        const dashboard = name === "dashboard";
        authPage.hidden = dashboard;
        dashPage.hidden = !dashboard;
        window.scrollTo({ top: 0 });
    };
    continueButton.addEventListener("click", () => showPage("dashboard"));
    backButton.addEventListener("click", () => showPage("auth"));
    showPage("auth");

    // The dashboard needs a signed-in account to do anything, so gate the step
    // forward on it and say why while it's disabled.
    subscribe((state) => {
        const signedIn = state.account !== null;
        continueButton.disabled = !signedIn;
        if (signedIn) {
            continueHint.clear();
        } else {
            continueHint.set(t("nav.signInFirst"), "muted");
        }
    });

    // Restores a cached account, or completes a redirect sign-in if we came back
    // from one. Must run after the panels exist so state changes reach them.
    const account = await initAuth();
    setState({ account });
}

void main();
